import React, { useState, useEffect } from 'react';

interface StatsCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  trend: string;
  color: 'red' | 'green' | 'purple' | 'cyan';
  onClick?: () => void;
}

const StatsCard: React.FC<StatsCardProps> = ({ icon, label, value, trend, color, onClick }) => {
  const [sparklineData, setSparklineData] = useState<number[]>([]);

  useEffect(() => {
    // Generate initial sparkline data (last 15 minutes, 15 data points)
    const generateData = () => {
      const baseValues: Record<string, number> = {
        'Active Threats': 3,
        'Auto-Remediated': 47,
        'Events Analyzed': 12400,
        'Avg Response Time': 1.2,
      };
      const base = baseValues[label] || 10;
      const variance = base * 0.3;
      return Array.from({ length: 15 }, () => 
        Math.max(0, base + (Math.random() - 0.5) * variance)
      );
    };

    setSparklineData(generateData());

    // Update every 60 seconds
    const interval = setInterval(() => {
      setSparklineData(prev => {
        const newData = [...prev.slice(1)];
        const base = prev[prev.length - 1] || 10;
        const variance = base * 0.2;
        newData.push(Math.max(0, base + (Math.random() - 0.5) * variance));
        return newData;
      });
    }, 60000);

    return () => clearInterval(interval);
  }, [label]);

  const colorClasses = {
    red: {
      bg: 'from-red-500/20 to-transparent',
      text: 'text-red-400',
      stroke: '#EF4444',
      fill: 'rgba(239, 68, 68, 0.1)',
    },
    green: {
      bg: 'from-green-500/20 to-transparent',
      text: 'text-green-400',
      stroke: '#22C55E',
      fill: 'rgba(34, 197, 94, 0.1)',
    },
    purple: {
      bg: 'from-arbitium-primary/20 to-transparent',
      text: 'text-arbitium-primary',
      stroke: '#8B5CF6',
      fill: 'rgba(139, 92, 246, 0.1)',
    },
    cyan: {
      bg: 'from-arbitium-accent/20 to-transparent',
      text: 'text-arbitium-accent',
      stroke: '#06B6D4',
      fill: 'rgba(6, 182, 212, 0.1)',
    },
  };

  const colors = colorClasses[color];

  // Generate sparkline path
  const generateSparklinePath = () => {
    if (sparklineData.length === 0) return '';
    const min = Math.min(...sparklineData);
    const max = Math.max(...sparklineData);
    const range = max - min || 1;
    
    const points = sparklineData.map((val, i) => {
      const x = (i / (sparklineData.length - 1)) * 100;
      const y = 30 - ((val - min) / range) * 25;
      return `${x},${y}`;
    });

    return `M ${points.join(' L ')}`;
  };

  const generateAreaPath = () => {
    if (sparklineData.length === 0) return '';
    const min = Math.min(...sparklineData);
    const max = Math.max(...sparklineData);
    const range = max - min || 1;
    
    const points = sparklineData.map((val, i) => {
      const x = (i / (sparklineData.length - 1)) * 100;
      const y = 30 - ((val - min) / range) * 25;
      return `${x},${y}`;
    });

    return `M 0,30 L ${points.join(' L ')} L 100,30 Z`;
  };

  return (
    <div 
      onClick={onClick}
      className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl p-4 cursor-pointer hover:border-arbitium-primary/30 hover:bg-arbitium-dark/70 transition-all group"
    >
      <div className="flex items-start justify-between mb-2">
        <div className={`p-2 rounded-lg bg-gradient-to-br ${colors.bg} ${colors.text}`}>
          {icon}
        </div>
        <span className="text-xs text-gray-500">{trend}</span>
      </div>
      
      <div className="flex items-end justify-between">
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-sm text-gray-400">{label}</p>
        </div>
        
        {/* Mini Sparkline */}
        <div className="w-24 h-8">
          <svg viewBox="0 0 100 30" className="w-full h-full" preserveAspectRatio="none">
            {/* Area fill */}
            <path
              d={generateAreaPath()}
              fill={colors.fill}
            />
            {/* Line */}
            <path
              d={generateSparklinePath()}
              fill="none"
              stroke={colors.stroke}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Click hint */}
      <div className="mt-2 text-xs text-gray-600 group-hover:text-gray-400 transition-colors">
        Click for details →
      </div>
    </div>
  );
};

export default StatsCard;
